import { lazy } from 'react';
import { PageItem } from 'types/Common';
import TransmissionActsPage from '../pages/transmission-acts';
import BenefitsPage from '../pages/benefits';
import BenefitCategoriesPage from '../pages/benefits-categories';
import RecognitionsListPage from '../pages/recognitions-list';
import RecognitionsSettingsPage from '../pages/recognitions-settings';
import RecognitionsAchievementsPage from '../pages/recognitions-achievements';
import RecognitionsAchievementDetailsPage from '../pages/recognitions-achievement-details';
import RecognitionsAllocationsPage from '../pages/recognitions-allocations';
import RecognitionsGiversPage from '../pages/recognitions-givers';
import RecognitionsRecipientsPage from '../pages/recognitions-recipients';
import RecognitionsLeaderboardPage from '../pages/recognitions-leaderboard';
import RecognitionsRewardsPage from '../pages/recognitions-rewards';
import RecognitionsBrandsPage from '../pages/recognitions-brands';
import RecognitionsRulesPage from '../pages/recognitions-rules';
import RecognitionsCommentsPage from '../pages/recognitions-comments';
import ShopInternalItemsPage from '../pages/shop-internal-items';
import ShopInternalItemDetailsPage from '../pages/shop-internal-item-details';
import ShopInternalInventoryPage from '../pages/shop-internal-inventory';
import ShopInternalInventoryDetailsPage from '../pages/shop-internal-inventory-details';
import ShopInternalSettingsPage from '../pages/shop-internal-settings';
import ExpensesPage from '../pages/expenses';
import ClaimsPage from '../pages/claims';
import ClaimsSettingsPage from '../pages/claims-settings';
import CompanyDetailsPage from '../pages/company-details';
import DiscountsPage from '../pages/discounts';
import DiscountsUsagePage from '../pages/discounts-usage';
import SurveySubmissionsTrainingPage from '../pages/survey-results-submissions-training';
import SurveyPendingSubmissionsTrainingPage from '../pages/survey-results-pending-submissions-training';
import SurveyDetailsPage from '../containers/surveys/survey-details/survey-details.component';
import SurveyResultsPage from '../pages/survey-results';
import SurveySubmissionPage from '../pages/survey-results-submission-details';
import AdminClientAdministratorsPage from '../pages/admin-client-admins';
import AdminClientCompaniesPage from '../pages/admin-client-companies';
import AdminClientCompanyDetailsPage from '../pages/admin-company-details';
import AdminClientSettingsPage from '../pages/admin-client-settings';
import AdminClientFeaturesPage from '../pages/admin-client-features';
import AdminClientDepositsPage from '../pages/admin-client-deposits';
import AdminClientDepositEventsPage from '../pages/admin-client-deposit-events';
import AdminClientDepositHistoryPage from '../pages/admin-client-deposit-history';
import AdminClientTransmissionActsPage from '../pages/admin-client-transmission-acts';
import AdminClientCompanyExpensesPage from '../pages/admin-client-company-expenses';
import AdminShopItemSettingsPage from '../pages/admin-shop-item-settings';
import AdminShopItemPricesPage from '../pages/admin-shop-item-prices';
import AdminShopItemContentPage from '../pages/admin-shop-item-content';
import AdminShopItemVouchersPage from '../pages/admin-shop-item-vouchers';
import AdminBrandSettingsPage from '../pages/admin-brand-settings';
import AdminBrandContentPage from '../pages/admin-brand-content';
import AdminBrandCardsPage from '../pages/admin-brand-cards';
import AdminBrandCardSettingsPage from '../pages/admin-card-settings';
import AdminBrandCardContentPage from '../pages/admin-card-content';
import AdminBrandCardPriceSettingsPage from '../pages/admin-card-price-settings';
import AdminBrandCardFeesPage from '../pages/admin-card-fees';
import AdminCardVouchersPage from '../pages/admin-card-vouchers';
import AdminDiscountsPage from '../pages/admin-discounts';
import AdminDiscountReportsPage from '../pages/admin-discount-reports';
import {
  GlobalPermissionModules,
  LocalPermissionModules,
  PermissionLevels,
} from '../constants/Administrators';
import { ROUTES } from './routes';

const Home = lazy(() => import('../containers/Home/Home'));
const CompanySettings = lazy(
  () => import('../containers/CompanySettings/CompanySettings'),
);
const AdministratorsInner = lazy(
  () =>
    import(
      '../containers/CompanySettings/views/Administrators/AdministratorsInner/AdministratorsInner'
    ),
);
const EmployeesPage = lazy(() => import('../pages/employees'));
const EmployeesInner = lazy(() => import('../pages/employee-details'));
const EmployeeGroupsPage = lazy(() => import('../pages/employee-groups'));
const EmployeesGroupDetailsPage = lazy(
  () => import('../pages/employee-group-details'),
);
const ChangePassword = lazy(() => import('../pages/auth/change-password'));
const Signin = lazy(() => import('../pages/auth/signin'));
const NewPassword = lazy(() => import('../pages/auth/new-password'));
const TwoFactor = lazy(() => import('../containers/TwoFactor/TwoFactor'));
const ForgotPassword = lazy(() => import('../pages/auth/forgot-password'));
const ResetPassword = lazy(() => import('../pages/auth/reset-password'));
const ClientsAdministrator = lazy(
  () => import('../containers/ClientsAdministrators/ClientsAdministrators'),
);
const BenefitTemplatesPage = lazy(
  () => import('../pages/admin-benefit-templates'),
);
const AdminBenefitDetailsPage = lazy(
  () => import('../pages/admin-benefit-details'),
);
const AdminSupplierDetailsPage = lazy(
  () => import('../pages/admin-supplier-details'),
);
const AdminSupplierPartnerPortalPage = lazy(
  () => import('../pages/admin-supplier-partner-portal'),
);
const AdminSupplierTrackingPage = lazy(
  () => import('../pages/admin-supplier-tracking'),
);
const IntegrationLogsPage = lazy(
  () =>
    import('../containers/IntegrationsOld/IntegrationLogs/IntegrationLogsPage'),
);
const IntegrationLogEventsPage = lazy(
  () => import('../pages/integration-log-events'),
);
const MarketplacePage = lazy(
  () => import('../containers/Marketplace/MarketplaceDeals/MarketplacePage'),
);
const AlternativeDetails = lazy(
  () =>
    import(
      '../containers/AlternativesOld/AlternativeDetails/AlternativeDetailsPage'
    ),
);
const SurveysPage = lazy(() => import('../pages/surveys'));
const TrainingsPage = lazy(() => import('../pages/trainings'));
const TrustboxPage = lazy(() => import('../pages/trustbox'));
const PageNotFound = lazy(
  () => import('../containers/PageNotFound/PageNotFound'),
);
const BenefitDetailsPage = lazy(() => import('../pages/benefit-details'));
const NewsPage = lazy(() => import('../pages/news'));
const NewDetailsPage = lazy(() => import('../pages/new-details'));
const NewsCategoryDetailsPage = lazy(
  () => import('../pages/news-category-details'),
);
const AdminClientsPage = lazy(() => import('../pages/admin-clients'));
const AdminAdminsPage = lazy(() => import('../pages/admin-admins'));
const AdminAdminDetailsPage = lazy(
  () => import('../pages/admin-admin-details'),
);
const AlternativesPage = lazy(() => import('../pages/alternatives'));
const ExternalExpensesPage = lazy(() => import('../pages/external-expenses'));
const AdminDepositBalancesPerClientPage = lazy(
  () => import('../pages/admin-deposit-balances-per-client'),
);
const AdminDepositBalancesPerCompanyPage = lazy(
  () => import('../pages/admin-deposit-balances-per-company'),
);
const AdminReportsPage = lazy(() => import('../pages/admin-reports'));
const AdminContentReviewsPage = lazy(
  () => import('../pages/admin-content-reviews'),
);
const AdminContentReviewDetailsPage = lazy(
  () => import('../pages/admin-content-review-details'),
);
const AdminTermsPage = lazy(() => import('../pages/admin-terms'));
const AdminOrdersPage = lazy(() => import('../pages/admin-orders'));
const AdminOrderPage = lazy(() => import('../pages/admin-order'));
const AdminDiscountDetailsPage = lazy(
  () => import('../pages/admin-discount-details'),
);
const AdminDiscountCodesPage = lazy(
  () => import('../pages/admin-discount-codes'),
);
const AdminIntegrationsPage = lazy(() => import('../pages/admin-integrations'));
const AdminIntegrationDetailsPage = lazy(
  () => import('../pages/admin-integration-details'),
);
const AdminSuppliersPage = lazy(() => import('../pages/admin-suppliers'));
const AdminShopItems = lazy(() => import('../pages/admin-shop-items'));
const AdminShopUsersPage = lazy(() => import('../pages/admin-shop-users'));
const AdminShopUserDetailsPage = lazy(
  () => import('../pages/admin-shop-user-details'),
);
const AdminBrandsPage = lazy(() => import('../pages/admin-brands'));
const AdminBrandPromptsPage = lazy(
  () => import('../pages/admin-brand-prompts'),
);
const AdminCardsPage = lazy(() => import('../pages/admin-cards'));
const OrdersPage = lazy(() => import('../pages/orders'));
const OrderInternalPage = lazy(() => import('../pages/order-internal'));
const ClaimDetailsPage = lazy(() => import('../pages/claim-details'));
const WalletsPage = lazy(() => import('../pages/wallets'));
const LogsPage = lazy(() => import('../pages/logs'));
const AutomationsPage = lazy(() => import('../pages/automations'));
const AutomationDetailsPage = lazy(() => import('../pages/automation-details'));
const BrandsPage = lazy(() => import('../pages/brands'));
const PublicSurveyFormPage = lazy(() => import('../pages/public-survey-form'));

export const pages = [
  {
    path: ROUTES.root.index,
    component: Home,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.public.surveyForm,
    component: PublicSurveyFormPage,
    public: true,
  },
  {
    path: ROUTES.transmissionActs.list,
    component: TransmissionActsPage,
    roles: ['hrAdmin'],
    permission: ['transmissionActs'],
  },
  {
    path: ROUTES.benefits.list,
    component: BenefitsPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: ROUTES.benefits.categories.list,
    component: BenefitCategoriesPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: ROUTES.benefits.details,
    component: BenefitDetailsPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: '/more/company-settings',
    component: CompanySettings,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
    permission: [
      'companies',
      'generalSettings',
      'integrations',
      'administrators',
    ],
  },
  {
    path: '/more/company-settings/integrations/:id/logs',
    component: IntegrationLogsPage,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
    permission: ['integrations'],
  },
  {
    path: ROUTES.integrations.logEvents,
    component: IntegrationLogEventsPage,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
    permission: ['integrations'],
  },
  {
    path: ROUTES.administrators.details,
    component: AdministratorsInner,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Administrators,
    permissionLevel: PermissionLevels.Global,
    permission: ['administrators'],
  },
  {
    path: ROUTES.companies.create,
    component: CompanyDetailsPage,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
    permission: ['companies'],
  },
  {
    path: ROUTES.companies.details,
    component: CompanyDetailsPage,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
    permission: ['companies'],
  },
  {
    path: ROUTES.employees.list,
    component: EmployeesPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.Employees,
    permissionLevel: PermissionLevels.Local,
    permission: ['employees'],
  },
  {
    path: ROUTES.employees.details,
    component: EmployeesInner,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.Employees,
    permissionLevel: PermissionLevels.Local,
    permission: ['employees'],
  },
  {
    path: ROUTES.auth.changePassword,
    component: ChangePassword,
    roles: ['hrAdmin', 'melpAdmin'],
  },
  {
    path: ROUTES.news.list,
    component: NewsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
    permission: ['news'],
  },
  {
    path: ROUTES.news.categories.create,
    component: NewsCategoryDetailsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
    permission: ['news'],
  },
  {
    path: ROUTES.news.categories.details,
    component: NewsCategoryDetailsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
    permission: ['news'],
  },
  {
    path: ROUTES.logs.list,
    component: LogsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.History,
    permissionLevel: PermissionLevels.Local,
    permission: ['systemLog'],
  },
  {
    path: ROUTES.auth.signin,
    component: Signin,
    public: true,
  },
  {
    path: ROUTES.auth.newPassword,
    component: NewPassword,
    public: true,
  },
  {
    path: ROUTES.employeeGroups.list,
    component: EmployeeGroupsPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.EmployeeGroups,
    permissionLevel: PermissionLevels.Global,
    permission: ['groups'],
  },
  {
    path: ROUTES.employeeGroups.details,
    component: EmployeesGroupDetailsPage,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Benefits,
      LocalPermissionModules.Employees,
    ],
    permissionLevel: PermissionLevels.Global,
    permission: ['groups'],
  },
  {
    path: ROUTES.news.details,
    component: NewDetailsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
    permission: ['news'],
  },
  {
    path: ROUTES.auth.twoFactor,
    component: TwoFactor,
    public: true,
  },
  {
    path: ROUTES.auth.forgotPassword,
    component: ForgotPassword,
    public: true,
  },
  {
    path: ROUTES.auth.resetPassword,
    component: ResetPassword,
    public: true,
  },
  {
    path: ROUTES.admin.deposits.balances.clients.list,
    component: AdminDepositBalancesPerClientPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.deposits.balances.companies.list,
    component: AdminDepositBalancesPerCompanyPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.admins.list,
    component: AdminAdminsPage,
    roles: ['melpAdmin'],
    adminModules: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.admins.create,
    component: AdminAdminDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.admins.details,
    component: AdminAdminDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.list,
    component: AdminClientsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.settings,
    component: AdminClientSettingsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.features,
    component: AdminClientFeaturesPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.deposits.list,
    component: AdminClientDepositsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.deposits.history,
    component: AdminClientDepositHistoryPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.depositEvents.list,
    component: AdminClientDepositEventsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.transmissionActs.list,
    component: AdminClientTransmissionActsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.administrators.list,
    component: AdminClientAdministratorsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.companies.list,
    component: AdminClientCompaniesPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.companies.details,
    component: AdminClientCompanyDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.expenses.details,
    component: AdminClientCompanyExpensesPage,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.clients.details.administrators.details,
    component: ClientsAdministrator,
    roles: ['melpAdmin'],
    adminModules: ['clientCompanies'],
  },
  {
    path: ROUTES.admin.integrations.list,
    component: AdminIntegrationsPage,
    roles: ['melpAdmin'],
    adminModules: ['integrations'],
  },
  {
    path: ROUTES.admin.integrations.details,
    component: AdminIntegrationDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['integrations'],
  },
  {
    path: ROUTES.admin.benefitTemplates.list,
    component: BenefitTemplatesPage,
    roles: ['melpAdmin'],
    adminModules: ['templates'],
  },
  {
    path: ROUTES.admin.benefitTemplates.details,
    component: AdminBenefitDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['templates'],
  },
  {
    path: ROUTES.admin.suppliers.list,
    component: AdminSuppliersPage,
    roles: ['melpAdmin'],
    adminModules: ['suppliers'],
  },
  {
    path: ROUTES.admin.suppliers.details,
    component: AdminSupplierDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['suppliers'],
  },
  {
    path: ROUTES.admin.suppliers.partnerPortal,
    component: AdminSupplierPartnerPortalPage,
    roles: ['melpAdmin'],
    adminModules: ['suppliers'],
  },
  {
    path: ROUTES.admin.suppliers.tracking,
    component: AdminSupplierTrackingPage,
    roles: ['melpAdmin'],
    adminModules: ['suppliers'],
  },
  {
    path: ROUTES.admin.shopItems.list,
    component: AdminShopItems,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.shopItems.details.settings,
    component: AdminShopItemSettingsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.shopItems.details.prices,
    component: AdminShopItemPricesPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.shopItems.details.content,
    component: AdminShopItemContentPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.shopItems.details.vouchers,
    component: AdminShopItemVouchersPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.shopUsers.list,
    component: AdminShopUsersPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.shopUsers.details,
    component: AdminShopUserDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.brands.list,
    component: AdminBrandsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.brands.prompts.list,
    component: AdminBrandPromptsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.brands.settings,
    component: AdminBrandSettingsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.brands.content,
    component: AdminBrandContentPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.brands.cards,
    component: AdminBrandCardsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.cards.list,
    component: AdminCardsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.cards.settings,
    component: AdminBrandCardSettingsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.cards.content,
    component: AdminBrandCardContentPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.cards.price,
    component: AdminBrandCardPriceSettingsPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.cards.fees,
    component: AdminBrandCardFeesPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.cards.vouchers,
    component: AdminCardVouchersPage,
    roles: ['melpAdmin'],
    adminModules: ['shopItems'],
  },
  {
    path: ROUTES.admin.reports,
    component: AdminReportsPage,
    roles: ['melpAdmin'],
    adminModules: ['reports'],
  },
  {
    path: ROUTES.admin.contentReviews.list,
    component: AdminContentReviewsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.contentReviews.details,
    component: AdminContentReviewDetailsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.terms.list,
    component: AdminTermsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.orders.list,
    component: AdminOrdersPage,
    roles: ['melpAdmin'],
    adminModules: ['orders'],
  },
  {
    path: ROUTES.admin.orders.details,
    component: AdminOrderPage,
    roles: ['melpAdmin'],
    adminModules: ['orders'],
  },
  {
    path: ROUTES.admin.discounts.list,
    component: AdminDiscountsPage,
    roles: ['melpAdmin'],
    adminModules: ['discounts'],
  },
  {
    path: ROUTES.admin.discounts.reports,
    component: AdminDiscountReportsPage,
    roles: ['melpAdmin'],
    adminModules: ['discounts'],
  },
  {
    path: ROUTES.admin.discounts.create,
    component: AdminDiscountDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['discounts'],
  },
  {
    path: ROUTES.admin.discounts.details.settings,
    component: AdminDiscountDetailsPage,
    roles: ['melpAdmin'],
    adminModules: ['discounts'],
  },
  {
    path: ROUTES.admin.discounts.details.codes,
    component: AdminDiscountCodesPage,
    roles: ['melpAdmin'],
    adminModules: ['discounts'],
  },
  {
    path: ROUTES.shop.external.list,
    component: MarketplacePage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: ROUTES.brands.list,
    component: BrandsPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: ROUTES.shop.internal.items.list,
    component: ShopInternalItemsPage,
    roles: ['hrAdmin'],
    permission: ['internalMerch'],
  },
  {
    path: ROUTES.shop.internal.items.create,
    component: ShopInternalItemDetailsPage,
    roles: ['hrAdmin'],
    permission: ['internalMerch'],
  },
  {
    path: ROUTES.shop.internal.items.details,
    component: ShopInternalItemDetailsPage,
    roles: ['hrAdmin'],
    permission: ['internalMerch'],
  },
  {
    path: ROUTES.shop.internal.inventory.list,
    component: ShopInternalInventoryPage,
    roles: ['hrAdmin'],
    permission: ['internalMerch'],
  },
  {
    path: ROUTES.shop.internal.inventory.details,
    component: ShopInternalInventoryDetailsPage,
    roles: ['hrAdmin'],
    permission: ['internalMerch'],
  },
  {
    path: ROUTES.shop.internal.settings,
    component: ShopInternalSettingsPage,
    roles: ['hrAdmin'],
    permission: ['internalMerch'],
  },
  {
    path: ROUTES.discounts.list,
    component: DiscountsPage,
    roles: ['hrAdmin'],
    permission: ['discounts'],
  },
  {
    path: ROUTES.discounts.usage,
    component: DiscountsUsagePage,
    roles: ['hrAdmin'],
    permission: ['discounts'],
  },
  {
    path: ROUTES.orders.list,
    component: OrdersPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
    permission: ['internalMerchOrders', 'melpShopOrders'],
  },
  {
    path: ROUTES.orders.internal.details,
    component: OrderInternalPage,
    roles: ['hrAdmin'],
    permission: ['internalMerchOrders'],
  },
  {
    path: ROUTES.alternatives.list,
    component: AlternativesPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.BenefitAlternatives,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: ROUTES.alternatives.details,
    component: AlternativeDetails,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.BenefitAlternatives,
    permissionLevel: PermissionLevels.Global,
    permission: ['benefits'],
  },
  {
    path: ROUTES.surveys.survey.list,
    component: SurveysPage,
    roles: ['hrAdmin'],
    permission: ['surveys'],
  },
  {
    path: ROUTES.surveys.training.list,
    component: TrainingsPage,
    roles: ['hrAdmin'],
    permission: ['trainings'],
  },
  {
    path: ROUTES.surveys.survey.details,
    component: SurveyDetailsPage,
    roles: ['hrAdmin'],
    permission: ['surveys'],
  },
  {
    path: ROUTES.surveys.survey.results,
    component: SurveyResultsPage,
    roles: ['hrAdmin'],
    permission: ['surveys'],
  },
  {
    path: ROUTES.surveys.training.details,
    component: SurveyDetailsPage,
    roles: ['hrAdmin'],
    permission: ['trainings'],
  },
  {
    path: ROUTES.surveys.training.results,
    component: SurveyResultsPage,
    roles: ['hrAdmin'],
    permission: ['trainings'],
  },
  {
    path: ROUTES.surveys.training.submissions.list,
    component: SurveySubmissionsTrainingPage,
    roles: ['hrAdmin'],
    permission: ['trainings'],
  },
  {
    path: ROUTES.surveys.training.submissions.pending.list,
    component: SurveyPendingSubmissionsTrainingPage,
    roles: ['hrAdmin'],
    permission: ['trainings'],
  },
  {
    path: ROUTES.surveys.training.submissions.details,
    component: SurveySubmissionPage,
    roles: ['hrAdmin'],
    permission: ['trainings'],
  },
  {
    path: ROUTES.surveys.form.details,
    component: SurveyDetailsPage,
    roles: ['hrAdmin'],
    permission: ['surveys'],
  },
  {
    path: ROUTES.surveys.form.results,
    component: SurveyResultsPage,
    roles: ['hrAdmin'],
    permission: ['surveys'],
  },
  {
    path: ROUTES.recognitions.list,
    component: RecognitionsListPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.settings,
    component: RecognitionsSettingsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.achievements.list,
    component: RecognitionsAchievementsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.achievements.create,
    component: RecognitionsAchievementDetailsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.achievements.details,
    component: RecognitionsAchievementDetailsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.allocations.list,
    component: RecognitionsAllocationsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.givers.list,
    component: RecognitionsGiversPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.recipients.list,
    component: RecognitionsRecipientsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.leaderboard.list,
    component: RecognitionsLeaderboardPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.rewards.list,
    component: RecognitionsRewardsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.brands.list,
    component: RecognitionsBrandsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.rules.list,
    component: RecognitionsRulesPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.recognitions.comments.list,
    component: RecognitionsCommentsPage,
    roles: ['hrAdmin'],
    permission: ['employeeRecognition'],
  },
  {
    path: ROUTES.trustBox.list,
    component: TrustboxPage,
    roles: ['hrAdmin'],
    permission: ['feedbackInbox'],
  },
  {
    path: ROUTES.automations.list,
    component: AutomationsPage,
    roles: ['hrAdmin'],
    permission: ['automations'],
  },
  {
    path: ROUTES.automations.create,
    component: AutomationDetailsPage,
    roles: ['hrAdmin'],
    permission: ['automations'],
  },
  {
    path: ROUTES.automations.details,
    component: AutomationDetailsPage,
    roles: ['hrAdmin'],
    permission: ['automations'],
  },
  {
    path: ROUTES.wallets.list,
    component: WalletsPage,
    roles: ['hrAdmin'],
    // TODO: not sure if correct permission
    permission: ['expenses'],
  },
  {
    path: ROUTES.expenses.list,
    component: ExpensesPage,
    roles: ['hrAdmin'],
    permission: ['expenses'],
  },
  {
    path: ROUTES.expenses.external.list,
    component: ExternalExpensesPage,
    roles: ['hrAdmin'],
    permission: ['expenses'],
  },
  {
    path: ROUTES.claims.list,
    component: ClaimsPage,
    roles: ['hrAdmin'],
    permission: ['expenseCompensations'],
  },
  {
    path: ROUTES.claims.settings,
    component: ClaimsSettingsPage,
    roles: ['hrAdmin'],
    permission: ['expenseCompensations'],
  },
  {
    path: ROUTES.claims.details,
    component: ClaimDetailsPage,
    roles: ['hrAdmin'],
    permission: ['expenseCompensations'],
  },
  {
    path: ROUTES.root.notFound,
    component: PageNotFound,
    public: true,
  },
] satisfies PageItem[];
